/**
 * Root 'App' Class:
 *   - manage bootstrap logic, like manage user login status, fetch initial data
 *   - initialize routers
 */
import _ from 'lodash';
import React, { Component } from 'react';
import IdleTimer from 'react-idle-timer';

import i18n from '../config/i18n';
import connectActionsAndReducers from '../toolbox/connectActionsAndReducers';
import {
  removeUserAuthDataFromLocalStorage,
  setUserLanguageToLocalStorage,
  getUserLanguageFromLocalStorage,
} from '../utils';


// component for layout
import Nav from '../components/shared/Nav';
import StatusBar from '../components/shared/StatusBar';
import AlertContainer from '../components/shared/AlertContainer';
import Footer from '../components/shared/Footer';

const publicPages = [
  '/',
  '/login',
  '/forgot',
  '/signup',
  '/connect-with-us',
  '/testimonials',
  '/subscription',
  '/product-intro',
  '/frequently-asked-questions',
  '/user-agreement',
  '/privacy-policy',
  '/privacy-statement',
];

class App extends Component {
  state = {
    route: null,
    params: null,
  };

  componentDidMount() {
    // make API calls
    const {
      actions,
      user,
    } = this.props;

    // sync up language between local storage, store and i18n
    const lang = getUserLanguageFromLocalStorage();
    if (lang && lang !== i18n.currentLocale()) {
      i18n.locale = lang;
    }
    actions.changeUserLanguage(i18n.currentLocale());

    // load the user profile data from API if:
    // 1. the profile object in the store is empty
    // 2. there's no active requests
    // 3. and user is not in the public pages or is logged in
    if (_.isEmpty(user.profile)
      && !user.isLoading
      && (!publicPages.includes(window.location.pathname) || user.isLoggedIn)
    ) {
      actions.getUserProfile();
    }

    // get app metadata which is used everywhere.
    actions.getMetadata();
  }

  handleTimeout = async () => {
    const {
      actions,
      user,
      router,
    } = this.props;

    if (user.isLoggedIn && !user.isLoggingOut) {
      await actions.logOut({
        onSucceed: () => { actions.createWarningAlert(i18n.t('sessionTimeoutMsg')); },
      });
      setTimeout(() => {
        router.navigate('/login', { trigger: true });
      }, 1000);
    }
  };

  changeLang = (language) => {
    const { actions } = this.props;
    actions.changeUserLanguage(language);
    i18n.locale = language;
    setUserLanguageToLocalStorage({ language });
  };

  verifyAccessToken = async () => {
    const {
      actions,
      user,
      router,
    } = this.props;

    if (user.isLoggedIn && !user.isLoggingOut) {
      await actions.isAccessTokenExist({
        onFail: () => {
          removeUserAuthDataFromLocalStorage();
          actions.createWarningAlert(i18n.t('tokenExpiredMsg'));
          setTimeout(() => {
            router.navigate('/login', { trigger: true });
          }, 2000);
        },
      });
    }
  };

  changeTeam = async (teamId) => {
    const {
      actions: {
        updateUser,
        createSuccessAlert,
      },
      router,
    } = this.props;

    if (await updateUser({ currentTeamId: teamId })) {
      createSuccessAlert(i18n.t('changeCurrentTeamSucceedMsg'));
      setTimeout(() => {
        router.navigate('/dashboard', { trigger: true });
      }, 2000);
    }
  };

  render() {
    const {
      alert,
      user: {
        isLoggedIn,
        profile: {
          roleId,
          firstName,
          email,
          currentTeamId,
        },
        teamList,
      },
      actions: {
        getJoinedTeamList,
      },
    } = this.props;

    return (
      /**
       * this is important to set throttle (UX is better than debounce)
       * with onAction property. Without them, we will fire too many
       * requests.
       */
      <IdleTimer
        timeout={1000 * 60 * 9} // 9 minutes
        onIdle={this.handleTimeout}
        onAction={this.verifyAccessToken}
        throttle={10000}
      >
        <div className="App">
          <Nav
            roleId={roleId}
            isLoggedIn={isLoggedIn}
            changeLang={this.changeLang}
            currentTeamId={currentTeamId}
            changeTeam={this.changeTeam}
            teamList={teamList}
            getTeamList={getJoinedTeamList}
          />

          { isLoggedIn && (
            <StatusBar
              firstName={firstName || ''}
              email={email || ''}
            />
          )}
          <AlertContainer alerts={alert.alerts} {...this.props} />
          <main id="main" style={{ minHeight: '500px' }} />
          <Footer />
        </div>
      </IdleTimer>
    );
  }
}

export default connectActionsAndReducers(App);



// WEBPACK FOOTER //
// ./src/containers/App.js